import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { cn, Tooltip, TooltipContent, TooltipTrigger } from '@newfront-insurance/core-ui/v2';
import { format } from 'date-fns';
import { ArchiveRestore, Check, Loader2, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { SettingsCard } from './settings-card';
import { type GetChatsByFiltersChat, GET_CHATS_BY_FILTERS } from '../../../queries/chats';
import { DELETE_CHAT, UNARCHIVE_CHAT } from '../../../queries/mutations';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { useCanDeleteArchivedChats } from '../hooks/use-permission';
import {
  ACTIVE_CHAT_STATUS,
  ARCHIVED_CHAT_STATUS,
  buildOptimisticChat,
  hasMutationErrors,
} from '../lib/chat-optimistic-updates';

type PendingAction = {
  action: 'delete' | 'unarchive';
  chatUuid: string;
};

type DeleteConfirmation = {
  chatUuid: string;
  isFading: boolean;
};

type DeleteChatMutationData = {
  deleteChat?: {
    __typename?: 'DeleteChatPayload';
    chatUuid: string;
    deleted: boolean;
  } | null;
};

type DeleteChatMutationVariables = {
  chatUuid: string;
};

const DELETE_CONFIRMATION_TIMEOUT_MS = 5_000;
const DELETE_CONFIRMATION_FADE_MS = 200;

function getArchivedChatMetadata(chat: GetChatsByFiltersChat) {
  const date = chat.lastMessageAt ?? chat.updatedAt ?? chat.createdAt;
  const formattedDate = date ? format(new Date(date), 'MMM d, yyyy, h:mm a') : '';
  const accountName = chat.account?.name;

  if (formattedDate && accountName) {
    return `${formattedDate} • ${accountName}`;
  }

  return formattedDate || accountName || 'No account';
}

function sortArchivedChats(chats: GetChatsByFiltersChat[]) {
  return [...chats].sort((left, right) => {
    const leftTime = new Date(left.lastMessageAt ?? left.updatedAt ?? left.createdAt ?? 0).getTime();
    const rightTime = new Date(right.lastMessageAt ?? right.updatedAt ?? right.createdAt ?? 0).getTime();
    return rightTime - leftTime;
  });
}

export function ArchivedChatsSettings() {
  const { userUuid, effectiveAppType, isPublicMode } = useChatOverlayContext();
  const canDeleteChats = useCanDeleteArchivedChats();
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation | null>(null);
  const archivedVariables = useMemo(
    () => ({
      userUuid,
      appType: effectiveAppType,
      statusFilter: ARCHIVED_CHAT_STATUS,
    }),
    [effectiveAppType, userUuid],
  );
  const activeVariables = useMemo(
    () => ({
      userUuid,
      appType: effectiveAppType,
      statusFilter: ACTIVE_CHAT_STATUS,
    }),
    [effectiveAppType, userUuid],
  );

  const { data, networkStatus } = useQuery(GET_CHATS_BY_FILTERS, {
    variables: archivedVariables,
    skip: isPublicMode || !userUuid,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const archivedChats = useMemo(() => sortArchivedChats(data?.chatsList ?? []), [data?.chatsList]);
  const isLoading = networkStatus === NetworkStatus.loading && data === undefined;

  const [unarchiveChat] = useMutation(UNARCHIVE_CHAT, {
    update(cache, { data: mutationData }) {
      const restoredChat = mutationData?.unarchiveChat;
      if (!restoredChat?.uuid) {
        return;
      }

      const archivedData = cache.readQuery({
        query: GET_CHATS_BY_FILTERS,
        variables: archivedVariables,
      });

      if (archivedData?.chatsList) {
        cache.writeQuery({
          query: GET_CHATS_BY_FILTERS,
          variables: archivedVariables,
          data: {
            chatsList: archivedData.chatsList.filter((chat) => chat.uuid !== restoredChat.uuid),
          },
        });
      }

      const activeData = cache.readQuery({
        query: GET_CHATS_BY_FILTERS,
        variables: activeVariables,
      });

      if (
        restoredChat.status === ACTIVE_CHAT_STATUS &&
        activeData?.chatsList &&
        !activeData.chatsList.some((chat) => chat.uuid === restoredChat.uuid)
      ) {
        cache.writeQuery({
          query: GET_CHATS_BY_FILTERS,
          variables: activeVariables,
          data: {
            chatsList: [restoredChat, ...activeData.chatsList],
          },
        });
      }
    },
  });

  const [deleteChat] = useMutation<DeleteChatMutationData, DeleteChatMutationVariables>(DELETE_CHAT, {
    update(cache, { data: mutationData }) {
      const deletedChatPayload = mutationData?.deleteChat;
      if (!deletedChatPayload?.deleted || !deletedChatPayload.chatUuid) {
        return;
      }

      const archivedData = cache.readQuery({
        query: GET_CHATS_BY_FILTERS,
        variables: archivedVariables,
      });

      if (archivedData?.chatsList) {
        cache.writeQuery({
          query: GET_CHATS_BY_FILTERS,
          variables: archivedVariables,
          data: {
            chatsList: archivedData.chatsList.filter((chat) => chat.uuid !== deletedChatPayload.chatUuid),
          },
        });
      }
    },
  });

  const deleteConfirmationChatUuid = deleteConfirmation?.chatUuid;
  useEffect(() => {
    if (!deleteConfirmationChatUuid) {
      return undefined;
    }

    const fadeTimeoutId = window.setTimeout(() => {
      setDeleteConfirmation((current) =>
        current?.chatUuid === deleteConfirmationChatUuid ? { ...current, isFading: true } : current,
      );
    }, DELETE_CONFIRMATION_TIMEOUT_MS);
    const resetTimeoutId = window.setTimeout(() => {
      setDeleteConfirmation((current) => (current?.chatUuid === deleteConfirmationChatUuid ? null : current));
    }, DELETE_CONFIRMATION_TIMEOUT_MS + DELETE_CONFIRMATION_FADE_MS);

    return () => {
      window.clearTimeout(fadeTimeoutId);
      window.clearTimeout(resetTimeoutId);
    };
  }, [deleteConfirmationChatUuid]);

  async function handleUnarchive(chatToUnarchive: GetChatsByFiltersChat) {
    if (!chatToUnarchive.uuid) {
      return;
    }

    setDeleteConfirmation(null);
    setPendingAction({ action: 'unarchive', chatUuid: chatToUnarchive.uuid });
    try {
      const result = await unarchiveChat({
        variables: { chatUuid: chatToUnarchive.uuid },
        optimisticResponse: {
          unarchiveChat: buildOptimisticChat(chatToUnarchive, { status: ACTIVE_CHAT_STATUS }),
        },
      });
      if (hasMutationErrors(result) || !result.data?.unarchiveChat) {
        throw new Error('Unarchive chat failed');
      }
    } catch {
      // Apollo removes the optimistic layer when the mutation fails.
    } finally {
      setPendingAction(null);
    }
  }

  async function handleDelete(chatToDelete: GetChatsByFiltersChat) {
    if (!chatToDelete.uuid) {
      return;
    }

    setDeleteConfirmation(null);
    setPendingAction({ action: 'delete', chatUuid: chatToDelete.uuid });
    try {
      const result = await deleteChat({
        variables: { chatUuid: chatToDelete.uuid },
        optimisticResponse: {
          deleteChat: {
            __typename: 'DeleteChatPayload',
            chatUuid: chatToDelete.uuid,
            deleted: true,
          },
        },
      });
      if (hasMutationErrors(result) || !result.data?.deleteChat?.deleted) {
        throw new Error('Delete chat failed');
      }
    } catch {
      // Apollo removes the optimistic layer when the mutation fails.
    } finally {
      setPendingAction(null);
    }
  }

  function handleDeleteClick(chatToDelete: GetChatsByFiltersChat) {
    if (!canDeleteChats || !chatToDelete.uuid) {
      return;
    }

    if (deleteConfirmation?.chatUuid === chatToDelete.uuid) {
      void handleDelete(chatToDelete);
      return;
    }

    setDeleteConfirmation({ chatUuid: chatToDelete.uuid, isFading: false });
  }

  return (
    <SettingsCard title="Archived chats" action={null} flushContent>
      <div className="flex flex-col">
        {isLoading && (
          <div className="px-5 py-4 font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#546A83]">
            Loading archived chats...
          </div>
        )}
        {!isLoading && archivedChats.length === 0 && (
          <div className="px-5 py-4 font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#546A83]">
            No archived chats
          </div>
        )}
        {archivedChats.map((chat, index) => {
          const pendingChatAction = pendingAction && pendingAction.chatUuid === chat.uuid ? pendingAction.action : null;
          const isPendingForChat = pendingChatAction !== null;
          const isDeletePending = pendingChatAction === 'delete';
          const isUnarchivePending = pendingChatAction === 'unarchive';
          const isConfirmingDelete = deleteConfirmation?.chatUuid === chat.uuid;

          return (
            <div
              key={chat.uuid}
              className={cn(
                'flex items-center justify-between gap-3 px-5 py-4',
                index > 0 && 'border-t border-[#DEE2E7]',
              )}
            >
              <div className="min-w-0">
                <p className="truncate font-['Open_Sans',sans-serif] text-[12px] leading-4 font-semibold text-[#262D46]">
                  {chat.title}
                </p>
                <p className="truncate font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#546A83]">
                  {getArchivedChatMetadata(chat)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {canDeleteChats && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={isConfirmingDelete ? `Confirm deletion for ${chat.title}` : `Delete ${chat.title}`}
                        className={cn(
                          'flex size-6 shrink-0 items-center justify-center rounded-xs text-[#546A83]',
                          'hover:bg-steel-100 hover:text-[#262D46] disabled:cursor-not-allowed disabled:opacity-50',
                        )}
                        disabled={!chat.uuid || isPendingForChat}
                        onClick={() => handleDeleteClick(chat)}
                      >
                        {isDeletePending ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : isConfirmingDelete ? (
                          <Check
                            className={cn(
                              'size-4 transition-opacity duration-200',
                              deleteConfirmation?.isFading ? 'opacity-0' : 'opacity-100',
                            )}
                          />
                        ) : (
                          <Trash2 className="size-4" />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{isConfirmingDelete ? 'Confirm deletion' : 'Delete chat'}</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`Unarchive ${chat.title}`}
                      className={cn(
                        'flex size-6 shrink-0 items-center justify-center rounded-xs text-[#546A83]',
                        'hover:bg-steel-100 hover:text-[#262D46] disabled:cursor-not-allowed disabled:opacity-50',
                      )}
                      disabled={!chat.uuid || isPendingForChat}
                      onClick={() => void handleUnarchive(chat)}
                    >
                      {isUnarchivePending ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <ArchiveRestore className="size-4" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Unarchive chat</TooltipContent>
                </Tooltip>
              </div>
            </div>
          );
        })}
      </div>
    </SettingsCard>
  );
}
